import { Box, Group, Tooltip, UnstyledButton } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { IconHands } from 'components/Icons/IconHands'

const SCORES = [1, 2, 3, 4, 5] as const

interface ScorePickerProps {
  value?: number
  onChange: (score: number) => void
  disabled?: boolean
  size?: number
  /** Default 'right' so the 5 hands hug the card edge (matches Figma). */
  position?: 'left' | 'center' | 'right' | 'apart'
  /** Mantine color name that drives the selected border + check badge.
   *  `green` for standards (content), `blue` for skills. Default `green`. */
  color?: 'green' | 'blue'
  /** Rubric text per score ("1".."5") shown as a hover tooltip (AR-828). */
  rubrics?: Record<string, string>
}

const COLOR_MAP = {
  green: 'var(--mantine-color-green-6)',
  blue: 'var(--mantine-color-blue-6)',
} as const

/**
 * 1-5 hand picker mirroring the prototype's ScoreSelector. Reuses the
 * project's IconHands SVGs for visual consistency with the rest of the app.
 */
export function ScorePicker({
  value,
  onChange,
  disabled = false,
  size = 56,
  position = 'right',
  color = 'green',
  rubrics,
}: ScorePickerProps) {
  const { t } = useTranslation()
  const accent = COLOR_MAP[color]
  const greyBorder = 'var(--mantine-color-gray-3)'
  const greyHand = 'var(--mantine-color-gray-7)'

  return (
    <Group spacing="xs" noWrap position={position}>
      {SCORES.map(score => {
        const isSelected = value === score
        // Same fallback chain as AssessmentInfo: per-item rubric first, then
        // the generic rubric_1..5 translations.
        const rubric = rubrics?.[String(score)] || t(`rubric_${score}`)
        const button = (
          <UnstyledButton
            key={score}
            onClick={() => {
              if (isSelected) return
              onChange(score)
            }}
            disabled={disabled}
            aria-label={`Score ${score}`}
            aria-pressed={isSelected}
            style={{
              position: 'relative',
              width: size,
              height: size,
              borderRadius: 8,
              border: `${isSelected ? 2 : 1}px solid ${isSelected ? accent : greyBorder}`,
              background: 'var(--mantine-color-white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
            }}
          >
            <IconHands
              value={score}
              width={Math.round(size * 0.55)}
              height={Math.round(size * 0.55)}
              color={isSelected ? accent : greyHand}
            />
            {isSelected && (
              <Box
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: accent,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconCheck size={10} stroke={3} />
              </Box>
            )}
          </UnstyledButton>
        )

        if (!rubric) return button

        return (
          <Tooltip
            key={score}
            label={rubric}
            withinPortal
            multiline
            width={260}
            position="top"
            events={{ hover: true, focus: true, touch: true }}
          >
            {button}
          </Tooltip>
        )
      })}
    </Group>
  )
}
